import type {
  AnalyticsContext,
  AnalyticsEventName,
  AnalyticsProperties,
  GameAnalyticsEvent,
  GameAnalyticsPort,
} from "../domain/analytics";
import { walletFlowProperties, type WalletFlowEvent } from "../domain/marketWalletFlow";

export type AnalyticsBatchSink = (events: readonly GameAnalyticsEvent[]) => Promise<void>;

type BufferedGameAnalyticsOptions = {
  readonly context: () => AnalyticsContext;
  readonly sink?: AnalyticsBatchSink;
  readonly now?: () => Date;
  readonly eventId?: () => string;
  readonly batchSize?: number;
  readonly maxBufferedEvents?: number;
  readonly storage?: Pick<Storage, "getItem" | "setItem">;
};

function randomEventId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `event-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Privacy-safe client event buffer. It never receives a Telegram id, username,
 * balance or free-form text. A backend adapter may provide a batch sink later;
 * without one the bounded snapshot remains available to test diagnostics.
 */
export class BufferedGameAnalytics implements GameAnalyticsPort {
  readonly #context: () => AnalyticsContext;
  readonly #sink: AnalyticsBatchSink | undefined;
  readonly #now: () => Date;
  readonly #eventId: () => string;
  readonly #batchSize: number;
  readonly #maxBufferedEvents: number;
  #events: GameAnalyticsEvent[] = [];
  #flushPromise: Promise<void> | null = null;
  #destroyed = false;
  #storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  #storageKey: string | null = null;

  constructor(options: BufferedGameAnalyticsOptions) {
    this.#context = options.context;
    this.#sink = options.sink;
    this.#now = options.now ?? (() => new Date());
    this.#eventId = options.eventId ?? randomEventId;
    this.#batchSize = Math.max(1, Math.trunc(options.batchSize ?? 40));
    this.#maxBufferedEvents = Math.max(this.#batchSize, Math.trunc(options.maxBufferedEvents ?? 250));
    try { this.#storage = options.storage ?? globalThis.localStorage; } catch { /* Storage denial never blocks play. */ }
  }

  /** Restore only after the authenticated market overview identifies the user. */
  bindUser(userId: string): void {
    const key = `diamond-tapper:wallet-diagnostics:v1:${encodeURIComponent(userId)}`;
    if (this.#storageKey === key) return;
    if (this.#storageKey) this.#events = this.#events.filter(event => event.name !== "market_wallet");
    this.#storageKey = key;
    try {
      const raw: unknown = JSON.parse(this.#storage?.getItem(key) ?? "[]");
      if (Array.isArray(raw)) for (const value of raw.slice(-100)) {
        if (!value || typeof value !== "object") continue;
        const event = value as Partial<GameAnalyticsEvent>;
        const id = event.eventId, occurredAt = event.occurredAt;
        if (typeof id !== "string" || typeof occurredAt !== "string") continue;
        const time = Date.parse(occurredAt), now = this.#now().getTime();
        if (event.name !== "market_wallet" || !/^[\w:-]{1,100}$/.test(id) || !Number.isFinite(time) || time < now - 86_400_000 || time > now + 60_000 || !event.properties) continue;
        const props = walletFlowProperties(event.properties as WalletFlowEvent);
        if (!props.phase || this.#events.some(row => row.eventId === event.eventId)) continue;
        this.#events.push({ eventId: id, name: "market_wallet", occurredAt,
          context: { ...this.#context(), clientVersion: typeof event.context?.clientVersion === "string" && /^[\w.-]{1,80}$/.test(event.context.clientVersion) ? event.context.clientVersion : "unknown" }, properties: props });
      }
    } catch { /* Ignore damaged or inaccessible local diagnostics. */ }
    if (this.#events.length > this.#maxBufferedEvents) this.#events.splice(0, this.#events.length - this.#maxBufferedEvents);
    this.#persist();
  }

  #persist(): void {
    if (!this.#storageKey) return;
    try { this.#storage?.setItem(this.#storageKey, JSON.stringify(this.#events.filter(event => event.name === "market_wallet" && Date.parse(event.occurredAt) >= this.#now().getTime() - 86_400_000).slice(-100))); }
    catch { /* Disk quota must never affect a transaction. */ }
  }

  track(name: AnalyticsEventName, properties: AnalyticsProperties = {}): void {
    if (this.#destroyed) return;
    this.#events.push(Object.freeze({
      eventId: this.#eventId(),
      name,
      occurredAt: this.#now().toISOString(),
      context: Object.freeze({ ...this.#context() }),
      properties: Object.freeze(name === "market_wallet" ? walletFlowProperties(properties as WalletFlowEvent) : { ...properties }),
    }));
    if (this.#events.length > this.#maxBufferedEvents) {
      this.#events.splice(0, this.#events.length - this.#maxBufferedEvents);
    }
    if (name === "market_wallet") this.#persist();
    if (this.#sink !== undefined && this.#events.length >= this.#batchSize) {
      void this.flush().catch(() => undefined);
    }
  }

  async flush(): Promise<void> {
    if (this.#sink === undefined || this.#events.length === 0 || this.#destroyed) return;
    if (this.#flushPromise !== null) return this.#flushPromise;
    const batch = this.#events.slice(0, this.#batchSize);
    this.#flushPromise = this.#sink(batch)
      .then(() => {
        const ids = new Set(batch.map((event) => event.eventId));
        this.#events = this.#events.filter((event) => !ids.has(event.eventId));
        this.#persist();
      })
      .finally(() => {
        this.#flushPromise = null;
      });
    return this.#flushPromise;
  }

  snapshot(): readonly GameAnalyticsEvent[] {
    return this.#events.map((event) => ({
      ...event,
      context: { ...event.context },
      properties: { ...event.properties },
    }));
  }

  destroy(): void {
    this.#destroyed = true;
  }
}
